import React, { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useToken from 'hooks/useToken';

export const ContabilidadContext = createContext();

export const ContabilidadProvider = ({ children }) => {
  const { token } = useToken();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [globalDateQuarter, setGlobalDateQuarter] = useState(new Date());
  const [empresas, setEmpresas] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);

  useEffect(() => {
    // Load from localStorage on mount
    const savedCompany = localStorage.getItem('contabilidadSelectedCompany');
    if (savedCompany) {
      try {
        setSelectedCompany(JSON.parse(savedCompany));
      } catch (e) {
        console.error("Error parsing saved company", e);
      }
    }
    
    const savedDate = localStorage.getItem('contabilidadDateQuarter');
    if (savedDate) {
      try {
        setGlobalDateQuarter(new Date(savedDate));
      } catch (e) {
        console.error("Error parsing saved date", e);
      }
    }
  }, []);

  useEffect(() => {
    // Save to localStorage when changed
    if (selectedCompany) {
      localStorage.setItem('contabilidadSelectedCompany', JSON.stringify(selectedCompany));
    } else {
      localStorage.removeItem('contabilidadSelectedCompany');
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (globalDateQuarter) {
      localStorage.setItem('contabilidadDateQuarter', globalDateQuarter.toISOString());
    }
  }, [globalDateQuarter]);

  const loadEmpresas = async () => {
    if (empresas.length > 0) return;
    setLoadingEmpresas(true);
    try {
      const response = await fetch('/api/contabilidad/libros/CuentasContables/ObtenerDatosFiscales', {
        headers: {
          'Authorization': 'Bearer ' + token
        }
      });
      const data = await response.json();
      if (data && data.iResponseType === 1) {
        setEmpresas(data.detalles || []);
        // If no company is selected and there's only one, auto-select it
        if (!selectedCompany && data.detalles?.length === 1) {
            setSelectedCompany(data.detalles[0]);
        }
      }
    } catch (error) {
      console.error("Error al cargar empresas", error);
    }
    setLoadingEmpresas(false);
  };

  const openCompanySelector = () => {
    loadEmpresas();
    setShowCompanyModal(true);
  };

  const closeCompanySelector = () => {
    setShowCompanyModal(false);
  };

  const changeCompany = (company) => {
    setSelectedCompany(company);
    setShowCompanyModal(false);
  };

  return (
    <ContabilidadContext.Provider
      value={{
        selectedCompany,
        openCompanySelector,
        empresas,
        loadingEmpresas,
        loadEmpresas,
        globalDateQuarter,
        setGlobalDateQuarter
      }}
    >
      {children}
      {showCompanyModal && (
        <CompanySelectorModal 
          show={showCompanyModal} 
          onHide={closeCompanySelector} 
          empresas={empresas} 
          onSelect={changeCompany} 
          loading={loadingEmpresas} 
          currentSelection={selectedCompany}
        />
      )}
    </ContabilidadContext.Provider>
  );
};

ContabilidadProvider.propTypes = {
  children: PropTypes.node
};

const CompanySelectorModal = ({ show, onHide, empresas, onSelect, loading, currentSelection }) => {
    return (
        <div className={`modal ${show ? 'd-block' : 'd-none'}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Seleccionar Empresa (Dato Fiscal)</h5>
                        {currentSelection && (
                          <button type="button" className="btn-close" onClick={onHide}></button>
                        )}
                    </div>
                    <div className="modal-body">
                        {loading ? (
                            <p>Cargando empresas...</p>
                        ) : (
                            <div className="list-group">
                                {empresas.map(emp => (
                                    <button 
                                      key={emp.idDatoFiscal} 
                                      type="button" 
                                      className={`list-group-item list-group-item-action ${currentSelection?.idDatoFiscal === emp.idDatoFiscal ? 'active' : ''}`}
                                      onClick={() => onSelect(emp)}
                                    >
                                        <strong>{emp.vRFC}</strong> - {emp.vRazonSocial}
                                    </button>
                                ))}
                                {empresas.length === 0 && <p>No hay datos fiscales disponibles.</p>}
                            </div>
                        )}
                    </div>
                    {currentSelection && (
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={onHide}>Cancelar</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

CompanySelectorModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  empresas: PropTypes.arrayOf(
    PropTypes.shape({
      idDatoFiscal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      vRFC: PropTypes.string,
      vRazonSocial: PropTypes.string
    })
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  currentSelection: PropTypes.shape({
    idDatoFiscal: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })
};
